/**
 * Custom element for color swatch selection in a product grid.
 */
class ColorSwatch extends HTMLElement {
  constructor() {
    super();
    this.productContainer = this.closest(".product-grid-item");
    if (!this.productContainer) return;
    this.onClick = this.onClick.bind(this);
    this.addEventListener("click", this.onClick, { passive: false });
  }

  /**
   * Handles click event on the swatch.
   * @param {MouseEvent} e
   */
  onClick(e) {
    e.preventDefault();

    const {
      title,
      price,
      url,
      handle,
      comparePrice = 0,
      mainImage,
      hoverImage
    } = this.dataset;
    this.#updateTitle(title);
    this.#updatePrice(Number(price), Number(comparePrice));
    this.#updateLinks(url);
    this.#updateHandle(handle);
    this.#updateMainImage(mainImage, title);
    this.#updateHoverImage(hoverImage);
    this.#markActive();
    this.#updateBadges();
  }

  /**
   * Updates the product title.
   * @param {string} title
   */
  #updateTitle(title) {
    const titleEl = this.productContainer?.querySelector(
      ".product-grid-item__title"
    );
    if (titleEl) titleEl.textContent = title;
  }

  /**
   * Updates the product price and compare price.
   * @param {number} price
   * @param {number} comparePrice
   */
  #updatePrice(price, comparePrice) {
    const priceEl = this.productContainer?.querySelector(
      ".product-grid-item__price"
    );
    if (!priceEl) return;

    const formattedPrice = this.#formatMoney(price);
    const formattedCompare =
      comparePrice > price ? this.#formatMoney(comparePrice) : "";

    priceEl.innerHTML = formattedCompare
      ? `<span class="product-grid-item__price-new">${formattedPrice}</span><s>${formattedCompare}</s>`
      : formattedPrice;
  }

  /**
   * Updates the main product image.
   * @param {string} mainImage
   * @param {string} alt
   */
  #updateMainImage(mainImage, alt) {
    const img = this.productContainer
      ?.querySelector("[data-product-media-container]")
      ?.querySelector("deferred-loading[data-product-media-featured]")
      ?.querySelector("figure[data-product-image]")
      ?.querySelector("img");
    if (!img) return;

    img.classList.add("loading");
    img.onload = () => img.classList.remove("loading");
    img.src = mainImage;
    img.alt = alt;
    img.removeAttribute("srcset");
    img.loading = "eager";
  }

  /**
   * Updates the hover image.
   * @param {string} hoverImage
   */
  #updateHoverImage(hoverImage) {
    const hoverImg = this.productContainer?.querySelector(
      "[data-product-media-container] figure[data-product-image-hover] img"
    );
    if (hoverImg && hoverImage) {
      hoverImg.src = hoverImage;
      hoverImg.srcset = "";
    }
  }

  /**
   * Marks this swatch as active.
   */
  #markActive() {
    this.parentElement
      ?.querySelectorAll("color-swatch")
      .forEach((swatch) => swatch.removeAttribute("aria-current"));
    this.setAttribute("aria-current", "true");
  }

  /**
   * Formats cents to currency string.
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }

  /**
   * Updates the product badges.
   */
  #updateBadges() {
    const badgesContainer = this.productContainer?.querySelector(
      "[data-product-badge]"
    );
    if (!badgesContainer) return;

    const customBadge = this.dataset.customBadge?.trim();
    const soldOut = this.hasAttribute("data-sold-out");
    const onSale = this.hasAttribute("data-on-sale");
    badgesContainer.innerHTML = "";

    if (customBadge) {
      badgesContainer.innerHTML += `
      <div class="product__badge__item product__badge__item--custom">
        <span>${customBadge}</span>
      </div>`;
    }

    if (soldOut) {
      badgesContainer.innerHTML += `
      <div class="product__badge__item product__badge__item--sold">
        <span>Sold out</span>
      </div>`;
    }

    if (onSale && !soldOut) {
      badgesContainer.innerHTML += `
      <div class="product__badge__item product__badge__item--sale">
        <span>Sale</span>
      </div>`;
    }

    if (!badgesContainer.innerHTML.trim()) {
      badgesContainer.innerHTML = ""; // Clean
    }
  }
  /**
   * Updates all links in the product card to point to the selected variant/product URL.
   */
  #updateLinks(url) {
    if (!url) return;
    const linkSelectors = [
      ".product-grid-item__title a",
      ".product-grid-item__price a",
      ".product__media__holder",
      ".btn--quick[data-button-quick-view]",
    ];

    linkSelectors.forEach((selector) => {
      this.productContainer?.querySelectorAll(selector)?.forEach((link) => {
        link.href = url;
      });
    });
  }

  #updateHandle(handle) {
    if (!handle) return;

    const quickBuyLink = this.productContainer?.querySelector('.product-grid-item__quick-buy [data-handle]');
    if (quickBuyLink) {
      quickBuyLink.setAttribute('data-handle', handle)
    }
  }
}

customElements.define("color-swatch", ColorSwatch);
